import { useEffect, useMemo, useRef, useState } from 'react'
import { CalendarDays, ChevronDown } from 'lucide-react'
import DatePicker from './DatePicker'

export type IsoRange = { start?: string | undefined; end?: string | undefined }

type RangeKey = 'all' | 'day' | 'week' | 'month' | 'year' | 'custom'
type Range = { start?: Date | undefined; end?: Date | undefined }

const RANGE_PRESETS: { key: RangeKey; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
]

const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

/** Resolve a preset (or custom dates) into an inclusive [start, end] range. */
function computeRange(key: RangeKey, customStart: string, customEnd: string): Range {
  const now = new Date()
  switch (key) {
    case 'day':
      return { start: startOfDay(now), end: endOfDay(now) }
    case 'week': {
      const diffToMonday = (now.getDay() + 6) % 7 // Mon-based week
      const monday = new Date(now)
      monday.setDate(now.getDate() - diffToMonday)
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      return { start: startOfDay(monday), end: endOfDay(sunday) }
    }
    case 'month':
      return {
        start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      }
    case 'year':
      return {
        start: startOfDay(new Date(now.getFullYear(), 0, 1)),
        end: endOfDay(new Date(now.getFullYear(), 11, 31)),
      }
    case 'custom':
      return {
        start: customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : undefined,
        end: customEnd ? endOfDay(new Date(`${customEnd}T00:00:00`)) : undefined,
      }
    case 'all':
    default:
      return {}
  }
}

function formatRangeLabel(key: RangeKey, range: Range): string {
  if (key === 'all') return 'All time'
  if (key === 'day' && range.start) return `Today · ${fmtDate(range.start)}`
  if (range.start && range.end) return `${fmtDate(range.start)} – ${fmtDate(range.end)}`
  if (range.start) return `From ${fmtDate(range.start)}`
  if (range.end) return `Until ${fmtDate(range.end)}`
  return 'All time'
}

/**
 * Reusable period filter. Owns its own range state and emits the resolved ISO
 * start/end to the parent, which refetches whatever it scopes.
 */
export default function AdminDateRangePicker({ onChange }: { onChange: (range: IsoRange) => void }) {
  const [open, setOpen] = useState(false)
  const [rangeKey, setRangeKey] = useState<RangeKey>('all')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const range = useMemo(() => computeRange(rangeKey, customStart, customEnd), [rangeKey, customStart, customEnd])
  const startISO = range.start?.toISOString()
  const endISO = range.end?.toISOString()

  // Emit the resolved range to the parent whenever it changes.
  useEffect(() => {
    onChange({ start: startISO, end: endISO })
    // onChange is stable enough for this use; keying on the ISO strings avoids loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startISO, endISO])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selectPreset = (key: RangeKey) => {
    setRangeKey(key)
    setOpen(false)
  }

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        className='flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700 shadow-sm hover:bg-gray-50'
      >
        <CalendarDays className='h-4 w-4 text-gray-500 flex-shrink-0' />
        <span className='whitespace-nowrap'>{formatRangeLabel(rangeKey, range)}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className='absolute right-0 z-20 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-lg'>
          <div className='flex flex-col'>
            {RANGE_PRESETS.map(preset => (
              <button
                key={preset.key}
                type='button'
                onClick={() => selectPreset(preset.key)}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  rangeKey === preset.key ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className='mt-2 border-t border-gray-100 pt-2'>
            <p className='px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400'>Custom range</p>
            <div className='space-y-2 px-1'>
              <DatePicker
                label='From'
                value={customStart}
                max={customEnd || undefined}
                onChange={val => { setCustomStart(val); setRangeKey('custom') }}
              />
              <DatePicker
                label='To'
                value={customEnd}
                min={customStart || undefined}
                onChange={val => { setCustomEnd(val); setRangeKey('custom') }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
